/**
 * Polygon layers for the globe.gl 3D globe.
 * Provides conflict zone overlays and CII (Country Instability Index) choropleth rendering.
 */

import type { GlobeInstance } from 'globe.gl';

// ---------------------------------------------------------------------------
// Conflict zone types and color constants
// ---------------------------------------------------------------------------

export type ConflictIntensity = 'high' | 'medium' | 'low';

export interface ConflictZonePolygon {
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  intensity: ConflictIntensity;
  name: string;
}

export const CONFLICT_CAP: Record<ConflictIntensity, string> = {
  high: 'rgba(255,40,40,0.25)',
  medium: 'rgba(255,120,0,0.20)',
  low: 'rgba(255,200,0,0.15)',
};

export const CONFLICT_SIDE: Record<ConflictIntensity, string> = {
  high: 'rgba(255,40,40,0.12)',
  medium: 'rgba(255,120,0,0.08)',
  low: 'rgba(255,200,0,0.06)',
};

export const CONFLICT_STROKE: Record<ConflictIntensity, string> = {
  high: '#ff3030',
  medium: '#ff8800',
  low: '#ffcc00',
};

export const CONFLICT_ALT: Record<ConflictIntensity, number> = {
  high: 0.006,
  medium: 0.004,
  low: 0.003,
};

// ---------------------------------------------------------------------------
// CII choropleth types and color constants
// ---------------------------------------------------------------------------

export type CIILevel = 'low' | 'normal' | 'elevated' | 'high' | 'critical';

export interface CIIPolygon {
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  countryCode: string;
  level: CIILevel;
  score: number;
}

export const CII_GLOBE_COLORS: Record<CIILevel, string> = {
  low: 'rgba(40, 180, 60, 0.35)',
  normal: 'rgba(220, 200, 50, 0.35)',
  elevated: 'rgba(240, 140, 30, 0.40)',
  high: 'rgba(220, 50, 20, 0.45)',
  critical: 'rgba(140, 10, 0, 0.50)',
};

/** Default altitude for CII choropleth polygons (flat against globe surface). */
const CII_ALT = 0.001;

/** Transparent side color for CII polygons (barely visible extrusion). */
const CII_SIDE = 'rgba(0,0,0,0.05)';

// ---------------------------------------------------------------------------
// Merged polygon discriminator
// ---------------------------------------------------------------------------

interface TaggedConflict extends ConflictZonePolygon {
  _type: 'conflict';
}

interface TaggedCII extends CIIPolygon {
  _type: 'cii';
}

type MergedPolygon = TaggedConflict | TaggedCII;

/**
 * Merges conflict zone and CII polygon arrays into a single dataset
 * with a `_type` discriminator so the globe accessors can choose the
 * correct color/altitude for each item.
 */
export function mergePolygonData(
  conflicts: ConflictZonePolygon[],
  cii: CIIPolygon[],
): object[] {
  const tagged: MergedPolygon[] = [
    ...conflicts.map((c) => ({ ...c, _type: 'conflict' as const })),
    ...cii.map((c) => ({ ...c, _type: 'cii' as const })),
  ];
  return tagged;
}

// ---------------------------------------------------------------------------
// Accessor helpers for merged data
// ---------------------------------------------------------------------------

// globe.gl's internal GeoJsonGeometry type uses a simplified `coordinates: number[]`
// which is structurally incompatible with proper GeoJSON Polygon/MultiPolygon types.
// At runtime the library handles nested coordinate arrays correctly, so we cast
// through `unknown` to bridge the type mismatch safely.

function getGeometry(d: object): unknown {
  return (d as MergedPolygon).geometry;
}

function getCapColor(d: object): string {
  const p = d as MergedPolygon;
  if (p._type === 'conflict') {
    return CONFLICT_CAP[p.intensity];
  }
  return CII_GLOBE_COLORS[p.level];
}

function getSideColor(d: object): string {
  const p = d as MergedPolygon;
  if (p._type === 'conflict') {
    return CONFLICT_SIDE[p.intensity];
  }
  return CII_SIDE;
}

function getStrokeColor(d: object): string | null {
  const p = d as MergedPolygon;
  if (p._type === 'conflict') {
    return CONFLICT_STROKE[p.intensity];
  }
  return null;
}

function getAltitude(d: object): number {
  const p = d as MergedPolygon;
  if (p._type === 'conflict') {
    return CONFLICT_ALT[p.intensity];
  }
  return CII_ALT;
}

function getLabel(d: object): string {
  const p = d as MergedPolygon;
  if (p._type === 'conflict') {
    return p.name;
  }
  return `${p.countryCode} — CII ${p.level} (${p.score.toFixed(1)})`;
}

// Geometry accessor cast — see comment above about globe.gl's simplified type.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GeoAccessor = (d: object) => any;

function conflictGeometry(d: object): unknown {
  return (d as ConflictZonePolygon).geometry;
}

function ciiGeometry(d: object): unknown {
  return (d as CIIPolygon).geometry;
}

// ---------------------------------------------------------------------------
// Configuration functions
// ---------------------------------------------------------------------------

/**
 * Configures the globe polygon layer to render conflict zone overlays.
 * Replaces any existing polygon data on the globe.
 */
export function configureConflictZones(
  globe: GlobeInstance,
  zones: ConflictZonePolygon[],
): void {
  globe
    .polygonsData(zones)
    .polygonGeoJsonGeometry(conflictGeometry as GeoAccessor)
    .polygonCapColor(
      (d: object) => CONFLICT_CAP[(d as ConflictZonePolygon).intensity],
    )
    .polygonSideColor(
      (d: object) => CONFLICT_SIDE[(d as ConflictZonePolygon).intensity],
    )
    .polygonStrokeColor(
      (d: object) => CONFLICT_STROKE[(d as ConflictZonePolygon).intensity],
    )
    .polygonAltitude(
      (d: object) => CONFLICT_ALT[(d as ConflictZonePolygon).intensity],
    )
    .polygonLabel((d: object) => (d as ConflictZonePolygon).name);
}

/**
 * Configures the globe polygon layer to render the CII choropleth.
 * Replaces any existing polygon data on the globe.
 */
export function configureCIIChoropleth(
  globe: GlobeInstance,
  countries: CIIPolygon[],
): void {
  globe
    .polygonsData(countries)
    .polygonGeoJsonGeometry(ciiGeometry as GeoAccessor)
    .polygonCapColor((d: object) => CII_GLOBE_COLORS[(d as CIIPolygon).level])
    .polygonSideColor(() => CII_SIDE)
    .polygonStrokeColor(() => null)
    .polygonAltitude(() => CII_ALT)
    .polygonLabel(
      (d: object) => {
        const p = d as CIIPolygon;
        return `${p.countryCode} — CII ${p.level} (${p.score.toFixed(1)})`;
      },
    );
}

/**
 * Configures the globe polygon layer to render both conflict zones and
 * CII choropleth simultaneously. Uses the `_type` discriminator added
 * by {@link mergePolygonData} to select the appropriate visual style.
 */
export function configureMergedPolygons(
  globe: GlobeInstance,
  conflicts: ConflictZonePolygon[],
  cii: CIIPolygon[],
): void {
  const merged = mergePolygonData(conflicts, cii);

  configureMergedPolygonAccessors(globe);
  globe.polygonsData(merged);
}

/**
 * Configures ONLY the merged-polygon accessors, leaving data untouched.
 * Called once at globe init so data setters can push `mergePolygonData`
 * output without re-installing accessors on every update.
 */
export function configureMergedPolygonAccessors(globe: GlobeInstance): void {
  globe
    .polygonGeoJsonGeometry(getGeometry as GeoAccessor)
    .polygonCapColor(getCapColor)
    .polygonSideColor(getSideColor)
    .polygonStrokeColor(getStrokeColor)
    .polygonAltitude(getAltitude)
    .polygonLabel(getLabel);
}
